// SettingsScreen - User preferences, account, and themes
import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Volume2, VolumeX, Trash2, FileText, UserCircle2, Palette, MessageSquare } from 'lucide-react';
import { Button } from '../common/Button';
import { PageContainer, GlassCard } from '../layout/PageContainer';
import { useTheme, THEMES } from '../../hooks/useTheme';
import { useTone, TONES } from '../../hooks/useTone';

export const SettingsScreen = ({
    isSoundEnabled,
    onToggleSound,
    onClearHistory,
    onShowDisclaimer,
    currentUser,
    isLoading
}) => {
    const isLoggedIn = currentUser && !currentUser.isAnonymous;
    const { currentTheme, setTheme, themes } = useTheme();
    const { currentTone, setTone, tones } = useTone();

    return (
        <PageContainer className="py-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md mx-auto"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        className="inline-block mb-3"
                        animate={{ rotate: [0, 180, 360] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    >
                        <Settings className="w-10 h-10 text-amber-400 mx-auto" />
                    </motion.div>
                    <h2 className="text-3xl font-serif text-amber-100">
                        Constellations of Choice
                    </h2>
                </div>

                {/* User Info */}
                {isLoggedIn && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6"
                    >
                        <GlassCard className="text-center border-amber-500/20">
                            <UserCircle2 className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                            <p className="text-amber-100 font-medium">
                                {currentUser.displayName || 'Cosmic Seeker'}
                            </p>
                            {currentUser.email && (
                                <p className="text-sm text-purple-400/70 mt-1">
                                    {currentUser.email}
                                </p>
                            )}
                        </GlassCard>
                    </motion.div>
                )}

                {/* Settings Options */}
                <div className="space-y-4">
                    {/* Sound Toggle */}
                    <SettingRow
                        icon={isSoundEnabled ? Volume2 : VolumeX}
                        label="Ambient Sound"
                        action={
                            <ToggleSwitch
                                enabled={isSoundEnabled}
                                onToggle={onToggleSound}
                            />
                        }
                    />

                    {/* Theme Selector */}
                    <GlassCard className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Palette className="w-5 h-5 text-amber-400" />
                            <span className="text-amber-100">Color Theme</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.values(themes).map((theme) => (
                                <ThemeOption
                                    key={theme.id}
                                    theme={theme}
                                    isSelected={currentTheme === theme.id}
                                    onSelect={() => setTheme(theme.id)}
                                />
                            ))}
                        </div>
                    </GlassCard>

                    {/* AI Tone Selector */}
                    <GlassCard className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <MessageSquare className="w-5 h-5 text-amber-400" />
                            <span className="text-amber-100">Reading Tone</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.values(tones).map((tone) => (
                                <ToneOption
                                    key={tone.id}
                                    tone={tone}
                                    isSelected={currentTone === tone.id}
                                    onSelect={() => setTone(tone.id)}
                                />
                            ))}
                        </div>
                    </GlassCard>

                    {/* Language (placeholder) */}
                    <SettingRow
                        icon={Settings}
                        label="Language"
                        action={
                            <span className="text-sm text-purple-400/70">
                                English (More soon!)
                            </span>
                        }
                    />

                </div>

                {/* Action Buttons */}
                <div className="mt-8 space-y-3">
                    <Button
                        variant="danger"
                        size="md"
                        onClick={onClearHistory}
                        icon={Trash2}
                        className="w-full"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Clearing...' : 'Clear Reading History'}
                    </Button>

                    <Button
                        variant="secondary"
                        size="md"
                        onClick={onShowDisclaimer}
                        icon={FileText}
                        className="w-full"
                    >
                        View App Disclaimer
                    </Button>
                </div>

                {/* Footer Info */}
                <motion.div
                    className="mt-12 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <p className="text-purple-400/60 text-sm">
                        Cosmic Compass v1.9.0
                    </p>
                    <p className="text-purple-400/40 text-xs mt-1">
                        Crafted with stardust and code - by Edo for Sabi ❤️
                    </p>
                    {currentUser?.uid && (
                        <p className="text-purple-400/30 text-[10px] mt-2">
                            User ID: {currentUser.uid}
                        </p>
                    )}
                </motion.div>
            </motion.div>
        </PageContainer>
    );
};

// Setting row component
const SettingRow = ({ icon: Icon, label, action }) => (
    <GlassCard className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-amber-400" />
            <span className="text-amber-100">{label}</span>
        </div>
        {action}
    </GlassCard>
);

// Toggle switch component
const ToggleSwitch = ({ enabled, onToggle }) => (
    <motion.button
        onClick={onToggle}
        className={`
            relative w-12 h-6 rounded-full transition-colors
            ${enabled ? 'bg-amber-500' : 'bg-white/10'}
        `}
        whileTap={{ scale: 0.95 }}
    >
        <motion.div
            className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow"
            animate={{ x: enabled ? 24 : 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
    </motion.button>
);

// Theme option button
const ThemeOption = ({ theme, isSelected, onSelect }) => {
    // Get preview color from theme
    const previewColor = theme.css['--theme-primary'];
    const accentColor = theme.css['--theme-accent'];

    return (
        <motion.button
            onClick={onSelect}
            className={`
                relative p-3 rounded-xl text-left transition-all
                ${isSelected
                    ? 'bg-white/10 border-2 border-amber-500/50'
                    : 'bg-white/5 border border-white/10 hover:border-white/20'
                }
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <div className="flex items-center gap-2 mb-1">
                {/* Color preview circles */}
                <div
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: previewColor }}
                />
                <div
                    className="w-3 h-3 rounded-full border border-white/20"
                    style={{ backgroundColor: accentColor }}
                />
            </div>
            <p className="text-sm text-amber-100 font-medium">{theme.name}</p>

            {/* Selected indicator */}
            {isSelected && (
                <motion.div
                    className="absolute top-1 right-1 w-2 h-2 bg-amber-400 rounded-full"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500 }}
                />
            )}
        </motion.button>
    );
};

// Tone option button
const ToneOption = ({ tone, isSelected, onSelect }) => {
    return (
        <motion.button
            onClick={onSelect}
            className={`
                relative p-3 rounded-xl text-left transition-all
                ${isSelected
                    ? 'bg-white/10 border-2 border-amber-500/50'
                    : 'bg-white/5 border border-white/10 hover:border-white/20'
                }
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{tone.emoji}</span>
            </div>
            <p className="text-sm text-amber-100 font-medium">{tone.name}</p>
            <p className="text-xs text-purple-300/60 mt-0.5">{tone.description}</p>

            {/* Selected indicator */}
            {isSelected && (
                <motion.div
                    className="absolute top-1 right-1 w-2 h-2 bg-amber-400 rounded-full"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500 }}
                />
            )}
        </motion.button>
    );
};

export default SettingsScreen;

